import { v4 as uuid } from 'uuid';

import {
  DoryMessageType,
  DoryServiceType,
  DoryStreamingMessage,
} from './StreamingMessages';

//#region Interfaces
export type IStreamingEventListeners = {
  [id: string]: {
    service?: DoryServiceType;
    messageType?: DoryMessageType;
    callback: (data: DoryStreamingMessage) => void;
  };
};
//#endregion

export class StreamingEventBus {
  private listeners: IStreamingEventListeners = {};
  private keys: string[] = [];

  public on<T extends DoryStreamingMessage = DoryStreamingMessage>(
    service: DoryServiceType,
    messageType: DoryMessageType,
    callback: (data: T) => void
  ): () => void;
  public on<T extends DoryStreamingMessage = DoryStreamingMessage>(
    service: DoryServiceType,
    messageType: DoryMessageType,
    callback: (data: T) => void,
    condition: (data: T) => boolean
  ): () => void;
  public on<T extends DoryStreamingMessage = DoryStreamingMessage>(
    service: DoryServiceType,
    messageType: DoryMessageType,
    callback: (data: T) => void,
    condition?: (data: T) => boolean
  ): () => void {
    let cb = callback;
    if (condition) {
      cb = (data: T) => {
        if (condition(data)) {
          callback(data);
        }
      };
    }

    const id = uuid();
    this.listeners[id] = {
      service,
      messageType,
      callback: cb,
    };
    this.keys.push(id);

    return () => this.unsubscribe(id);
  }

  public once<T extends DoryStreamingMessage = DoryStreamingMessage>(
    service: DoryServiceType,
    messageType: DoryMessageType,
    callback: (data: T) => void
  ): () => void;
  public once<T extends DoryStreamingMessage = DoryStreamingMessage>(
    service: DoryServiceType,
    messageType: DoryMessageType,
    callback: (data: T) => void,
    condition: (data: T) => boolean
  ): () => void;
  public once<T extends DoryStreamingMessage = DoryStreamingMessage>(
    service: DoryServiceType,
    messageType: DoryMessageType,
    callback: (data: T) => void,
    condition?: (data: T) => boolean
  ): () => void {
    const unsubscribe = this.on<T>(service, messageType, (data: T) => {
      if (!condition || condition(data)) {
        callback(data);
        unsubscribe();
      }
    });
    return unsubscribe;
  }

  public all(callback: (data: DoryStreamingMessage) => void): () => void {
    const id = uuid();
    this.listeners[id] = {
      callback,
    };
    this.keys.push(id);

    return () => this.unsubscribe(id);
  }

  public unsubscribe(id: string): void {
    const index = this.keys.indexOf(id);
    if (index !== -1) {
      delete this.listeners[id];
      this.keys.splice(index, 1);
    }
  }

  public emit<T extends DoryStreamingMessage = DoryStreamingMessage>(
    service: DoryServiceType,
    messageType: DoryMessageType,
    data: T
  ): void {
    this.keys.forEach((key) => {
      const elem = this.listeners[key];
      if (
        (elem.service === undefined || elem.service === service) &&
        (elem.messageType === undefined || elem.messageType === messageType)
      ) {
        elem.callback(data);
      }
    });
  }
}
